import { Cabin, IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import dayjs from 'dayjs'
import { orderBy } from 'lodash-es'
import { Direction } from '../../bewotec/openapi/models/Direction'
import { RailTransportType } from '../../bewotec/openapi/models/RailTransportType'
import { handleError, handleWarning } from '../../general/errorHandling'
import { DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT } from '../../general/helper'
import { CLASSIC, CLASSICAI, durations, LIGHT, PAUSCHAL, PAUSCHALAI } from '../../general/master-data'
import { IPaxConfig } from '../catalog/interfaces'
import { CabinCategory } from '../details/cabin/interfaces'
import { convertIBEPriceModel } from '../details/cruise/helper'
import { IDetailCruise, IDetailRoute, IDetailShip } from '../details/cruise/interfaces'
import { getAirlineNames } from '../details/flex-flight/helper'
import { IExpinetFlexFlightType } from '../details/flex-flight/interfaces'
import { IExpinetFlightType } from '../details/flight/interfaces'
import { mapPriceModelLabel, mapPriceModelLabelForTracking } from '../details/helper'
import { ITransportationTotal } from '../details/price/interfaces'
import { IFetchRailOption, RailDirection } from '../details/rail/interfaces'
import {
  AdobeEvent,
  CruiseViewTrackingEvent,
  CruiseViewTrackingEventParams,
  FilterData,
  FilterSelectTrackingEvent,
  FilterSelectTrackingEventParams,
  PDFButtonTrackingEvent,
  PDFButtonTrackingEventParams,
  SortTrackingEvent,
  TrackingCabin,
  TrackingCruise,
  TrackingCruisePDF,
} from './interface'

/* eslint-disable no-bitwise */

const convertCabinPosition = (cabinPosition: string) => {
  switch (cabinPosition) {
    case 'vorne':
      return 'V'
    case 'mittig':
      return 'M'
    case 'hinten':
      return 'H'
    default:
      return 'n/a'
  }
}

export const addAdobeConsent = (url: any) => {
  // @ts-ignore
  const optanonConsent = window._satellite?.cookie?.get('OptanonConsent')
  const prevPage = window.location.href

  if (!!optanonConsent && optanonConsent.includes('C0002:1') && optanonConsent.includes('C0004:1')) {
    // @ts-ignore
    const visitor = window.Visitor.getInstance('BCF356A25FBF85370A495EA3@AdobeOrg', {
      overwriteCrossDomainMCIDAndAID: true,
    })

    const arr = url.split('#')
    let mutatedUrl =
      visitor.appendVisitorIDsTo(arr[0]) +
      '&optanonconsent=' +
      encodeURIComponent(optanonConsent) +
      '&prev_page=' +
      encodeURIComponent(prevPage)

    if (typeof arr[1] != 'undefined') {
      return (mutatedUrl += '#' + arr[1])
    }

    return mutatedUrl
  }

  return url
}

export const addTrackingEvent = (event: AdobeEvent | undefined, origin: string) => {
  if (!event) {
    handleError({ error: new Error('Tracking event is undefined'), origin })
    return
  }

  // otherwise if user uses adblocker we get an error
  if (!!window.adobeDataLayer) {
    window.adobeDataLayer.push(event)
  }
}

export const buildSortTrackingEvent = (sortKey: string, sortOrder: number) => {
  const event: SortTrackingEvent = {
    event: 'sort',
    sort: {
      term: mapPriceModelLabel(sortKey as IbePriceModelCode),
      order: sortOrder === 1 ? 'ascending' : 'descending',
    },
  }

  return event
}

export const buildCruiseViewTrackingEvent = (params: CruiseViewTrackingEventParams) => {
  if (
    !params.cruise ||
    !params.route ||
    !params.ship ||
    !params.category ||
    (!params.cabin && !params.category.priceModel)
  ) {
    handleWarning('Can not create cruise view tracking event', {
      cruise: params.cruise,
      route: params.route,
      ship: params.ship,
      category: params.category,
      cabin: params.cabin,
      priceModel: params.category?.priceModel,
    })
    return
  }

  const event: CruiseViewTrackingEvent = {
    event: 'cruiseView',
    booking: {
      event: 'prodView',
      price: params.totalPrice.toFixed(2),
    },
    cruise: getCruise(params.route, params.cruise, params.ship, params.mandatoryFlight),
    eventInfo: {
      positionOfResult: params.position + 1,
    },
  }

  event.cabin = getCabin(params.category, params.cabin, params.paxConfig, params.cabinPrice)

  const outboundFlight = params.flight?.outboundFlight
  const inboundFlight = params.flight?.inboundFlight
  const isBothWays = !!inboundFlight && !!outboundFlight

  if (!!outboundFlight && !!event.cabin.product) {
    const outboundFlightProduct = getTransportationFlight(outboundFlight, null, isBothWays, params.transportationTotal)
    event.cabin.product.push(outboundFlightProduct)
  }

  if (!!inboundFlight && event.cabin.product) {
    const inboundFlightProduct = getTransportationFlight(null, inboundFlight, isBothWays, params.transportationTotal)
    event.cabin.product.push(inboundFlightProduct)
  }

  return event
}

export const buildPDFButtonTrackingEvent = (params: PDFButtonTrackingEventParams) => {
  const startDate = params.cruise?.startDate ? params.cruise?.startDate.replaceAll('.', '-') : ''
  const pdfFileName = `AIDA_Angebot_${params.ship?.name}_${startDate}.pdf`

  const cruise = getCruise(params.route!, params.cruise!, params.ship!, params.mandatoryFlight) as TrackingCruisePDF
  cruise.includedFlight = !!params.flight?.inboundFlight || !!params.flight?.outboundFlight ? 'yes' : 'no'

  const event: PDFButtonTrackingEvent = {
    event: 'download',
    download: {
      link: pdfFileName,
    },
    booking: {
      tabCount: '1',
      price: params.totalPrice.toFixed(2),
    },
    cruise,
  }

  if (
    (params.category?.priceModel || params.priceModel!) &&
    [CLASSIC, CLASSICAI, LIGHT, PAUSCHAL, PAUSCHALAI].includes(params.category?.priceModel ?? params.priceModel!)
  ) {
    event.cabin = {
      type: params.category?.id || 'n/a',
      cabinNumber: 'n/a',
      position: 'n/a',
      priceModel: convertIBEPriceModel(params.category?.priceModel ?? params.priceModel!),
    }
  } else {
    event.cabin = {
      type: params.cabin?.cabinCategory || 'n/a',
      cabinNumber: params.cabin?.id || 'n/a',
      position: !!params.cabin?.position ? convertCabinPosition(params.cabin.position) : 'n/a',
      priceModel: convertIBEPriceModel(params.priceModel!),
    }
  }

  event.cabin.paxConfig = `${params.paxConfig.adults}:${params.paxConfig.juveniles}:${params.paxConfig.children}:${params.paxConfig.babies}`
  event.cabin.product = [
    {
      name: params.type || 'n/a',
      type: 'cabin',
      price: params.cabinPrice.toFixed(2),
    },
  ]

  if (params.transportation === 'Wunschflug') {
    if (!!params.flexFlight?.outboundFlight) {
      event.cabin.product.push(getTransportationFlexFlight(params.flexFlight!, Direction.O, params.transportationTotal))
    } else {
      event.cabin.product.push(
        getTransportationIndividual('Individuelle Anreise', 'outbound', params.transportationTotal.amount.toFixed(2))
      )
    }
    if (!!params.flexFlight?.inboundFlight) {
      event.cabin.product.push(getTransportationFlexFlight(params.flexFlight!, Direction.R, params.transportationTotal))
    } else {
      event.cabin.product.push(
        getTransportationIndividual('Individuelle Abreise', 'return', params.transportationTotal.amount.toFixed(2))
      )
    }

    if (!!params.railOptionRNF && !('error' in params.railOptionRNF)) {
      if (['inbound', 'both'].includes(params.railDirectionRNF)) {
        event.cabin.product.push(
          getTransportationRail('inbound', params.railOptionRNF, params.railDirectionRNF, params.transportationTotal)
        )
      }
      if (['outbound', 'both'].includes(params.railDirectionRNF)) {
        event.cabin.product.push(
          getTransportationRail('outbound', params.railOptionRNF, params.railDirectionRNF, params.transportationTotal)
        )
      }
    }
  } else if (params.transportation === 'Flug') {
    const inboundFlight = params.flight?.inboundFlight
    const outboundFlight = params.flight?.outboundFlight
    const isBothWays = !!inboundFlight && !!outboundFlight

    if (!!inboundFlight) {
      event.cabin.product.push(getTransportationFlight(null, inboundFlight, isBothWays, params.transportationTotal))
    } else {
      event.cabin.product.push(
        getTransportationIndividual('Individuelle Abreise', 'return', params.transportationTotal.amount.toFixed(2))
      )
    }
    if (!!outboundFlight) {
      event.cabin.product.push(getTransportationFlight(outboundFlight, null, isBothWays, params.transportationTotal))
    } else {
      event.cabin.product.push(
        getTransportationIndividual('Individuelle Anreise', 'outbound', params.transportationTotal.amount.toFixed(2))
      )
    }

    if (!!params.railOptionRNF && !('error' in params.railOptionRNF)) {
      if (['inbound', 'both'].includes(params.railDirectionRNF)) {
        event.cabin.product.push(
          getTransportationRail('inbound', params.railOptionRNF, params.railDirectionRNF, params.transportationTotal)
        )
      }
      if (['outbound', 'both'].includes(params.railDirectionRNF)) {
        event.cabin.product.push(
          getTransportationRail('outbound', params.railOptionRNF, params.railDirectionRNF, params.transportationTotal)
        )
      }
    }
  } else if (params.transportation === 'Bahn' && !!params.railOption && !('error' in params.railOption)) {
    if (['inbound', 'both'].includes(params.railDirection)) {
      event.cabin.product.push(
        getTransportationRail('inbound', params.railOption, params.railDirection, params.transportationTotal)
      )
    }
    if (['outbound', 'both'].includes(params.railDirection)) {
      event.cabin.product.push(
        getTransportationRail('outbound', params.railOption, params.railDirection, params.transportationTotal)
      )
    }
  } else if (params.transportation === 'Individuell' || (params.transportation === 'Bahn' && !params.railOption)) {
    event.cabin.product.push(getTransportationIndividual('Individuelle Anreise', 'outbound', '0.00'))
    event.cabin.product.push(getTransportationIndividual('Individuelle Abreise', 'inbound', '0.00'))
  }

  return event
}

export const buildFilterSelectTrackingEvent = (params: FilterSelectTrackingEventParams) => {
  const displayedDurations = !!params.selection.duration
    ? durations
        .filter(duration => !!params.selection.duration && params.selection.duration & duration.key)
        .map(duration => duration.label)
    : []
  const promos = orderBy(mapTrackingItem(params.data.campaigns, params.selection.campaigns))
  const destinations = orderBy(mapTrackingItem(params.data.regions, params.selection.regions))
  const airports = orderBy(mapTrackingItem(params.data.airports, params.selection.airports, true))
  const cabinCategories = mapTrackingItem(params.data.categories, params.selection.categories)
  const ships = orderBy(mapTrackingItem(params.data.ships, params.selection.ships))
  const advanced = orderBy(mapTrackingItem(params.data.cruiseAttributes, params.selection.cruiseAttributes))
  const priceModels = !!Array.isArray(params.selection.priceModels)
    ? orderBy(
        params.selection.priceModels.map((priceModel: string) =>
          mapPriceModelLabelForTracking(priceModel as IbePriceModelCode)
        )
      )
    : []

  const event: FilterSelectTrackingEvent = {
    event: 'filterSelect',
    filter: {
      duration: displayedDurations,
      promo: promos,
      destination: destinations,
      paxConfig: `${params.selection.adults}:${params.selection.juveniles}:${params.selection.children}:${params.selection.babies}`,
      pointOfDeparture: airports,
      wunschflug: !!params.selection.hasFlexFlight,
      cabinName: cabinCategories,
      priceModel: priceModels,
      ship: ships,
      advanced,
      numberOfResults: params.totalResults,
      ports: params.selection.ports,
      isStartPort: params.selection.isStartPort,
      isEndPort: params.selection.isEndPort,
    },
  }

  if (!!params.selection.departureDate)
    event.filter.startDate = dayjs(params.selection.departureDate.toString(), DEFAULT_DATE_FORMAT).format(
      ISO_DATE_FORMAT
    )
  if (!!params.selection.arrivalDate)
    event.filter.endDate = dayjs(params.selection.arrivalDate.toString(), DEFAULT_DATE_FORMAT).format(ISO_DATE_FORMAT)

  return event
}

const getCruise = (
  route: IDetailRoute,
  cruise: IDetailCruise,
  ship: IDetailShip,
  mandatoryFlight: boolean
): TrackingCruise => {
  const daysToCruise = Math.round(
    (new Date(dayjs(cruise?.startDate, DEFAULT_DATE_FORMAT).toString()).getTime() - new Date().getTime()) /
      (1000 * 60 * 60 * 24)
  )

  return {
    name: `${route.name} ${cruise.duration} Tage mit ${ship.name}, ${cruise.startDate} bis ${cruise.endDate}`,
    ID: cruise.id,
    ship: ship.name,
    departurePort: route.ports[0],
    arrivalPort: route.ports[route.ports.length - 1],
    route: route.name,
    destination: cruise.regions,
    duration: cruise.duration.toString(),
    dateRange:
      dayjs(cruise.startDate, DEFAULT_DATE_FORMAT).format(ISO_DATE_FORMAT) +
      ' bis ' +
      dayjs(cruise.endDate, DEFAULT_DATE_FORMAT).format(ISO_DATE_FORMAT),
    daysToCruise: daysToCruise.toString(),
    promo: getPromos(cruise),
    mandatoryFlight: mandatoryFlight ? 'yes' : 'no',
  }
}

const getCabin = (
  category: CabinCategory,
  cabin: Cabin | null,
  paxConfig: IPaxConfig,
  cabinPrice: number
): TrackingCabin => {
  return {
    type: category.type,
    cabinNumber: cabin?.id || 'n/a',
    position: !!cabin ? convertCabinPosition(cabin.position) : 'n/a',
    priceModel: mapPriceModelLabelForTracking(category.priceModel),
    paxConfig: `${paxConfig.adults}:${paxConfig.juveniles}:${paxConfig.children}:${paxConfig.babies}`,
    product: [
      {
        name: category.type,
        type: 'cabin',
        price: cabinPrice.toFixed(2),
      },
    ],
  }
}

const getPromos = (cruise: IDetailCruise) => {
  const promos = []

  if (cruise.isAidaSelection) {
    promos.push('AIDAselection')
  }

  if (!!cruise.campaignEnd) {
    promos.push('Aktion')
  }

  return promos.join(', ')
}

const getTransportationIndividual = (name: string, direction: string, totalPrice: string) => {
  return {
    name,
    type: 'transfer',
    price: '0.00',
    transfer: {
      type: 'individual',
      direction: direction === 'outbound' ? 'outbound' : 'return',
      totalPrice,
    },
  }
}

const getTransportationFlight = (
  outboundFlight: IExpinetFlightType | null,
  inboundFlight: IExpinetFlightType | null,
  isBothWays: boolean,
  transportationTotal: ITransportationTotal
) => {
  const flight = outboundFlight || inboundFlight

  return {
    name: !!outboundFlight ? 'Fluganreise' : 'Flugabreise',
    type: 'transfer',
    price: isBothWays ? (flight!.price / 2).toFixed(2) : flight!.price.toFixed(2),
    info: 'Rail&Fly',
    transfer: {
      type: 'plane',
      direction: !!outboundFlight ? 'outbound' : 'return',
      class: flight!.mainFlight.flightClass.name,
      brand: getAirlineNames(flight!),
      pointOfDeparture: flight!.departure.airportId,
      pointOfArrival: flight!.arrival.airportId,
      totalPrice: transportationTotal.amount.toFixed(2),
    },
  }
}

const getTransportationFlexFlight = (
  flight: IExpinetFlexFlightType,
  direction: Direction,
  transportationTotal: ITransportationTotal
) => {
  const isOutbound = direction === Direction.O
  const flightByDirection = isOutbound ? flight.outboundFlight : flight.inboundFlight
  const isBothWays = !!flight.outboundFlight && !!flight.inboundFlight

  return {
    name: isOutbound ? 'Wunschfluganreise' : 'Wunschflugabreise',
    type: 'transfer',
    price: isBothWays ? (flight!.price / 2).toFixed(2) : flight!.price.toFixed(2),
    transfer: {
      type: 'plane',
      direction: isOutbound ? 'outbound' : 'return',
      class: flightByDirection!.mainFlight.flightClass.name,
      brand: getAirlineNames(flightByDirection!),
      pointOfDeparture: flightByDirection!.departure.airportId,
      pointOfArrival: flightByDirection!.arrival.airportId,
      totalPrice: transportationTotal.amount.toFixed(2),
    },
  }
}

const getTransportationRail = (
  direction: string,
  rail: IFetchRailOption,
  selectedRailDirection: RailDirection,
  transportationTotal: ITransportationTotal
) => {
  return {
    name: direction === 'outbound' ? 'Zuganreise' : 'Zugabreise',
    type: 'transfer',
    price: selectedRailDirection === 'both' ? (rail.price / 2).toFixed(2) : rail.price.toFixed(2),
    info: rail.type === RailTransportType.RIT ? 'Rail&Cruise' : 'Rail&Fly',
    ID: 'n/a',
    transfer: {
      type: 'train',
      direction: direction === 'outbound' ? 'outbound' : 'return',
      class: rail.railClassCode,
      totalPrice: transportationTotal.amount.toFixed(2),
    },
  }
}

const mapTrackingItem = (
  filterData: FilterData | null,
  selectedItems: string[],
  useKeyProperty?: boolean
): string[] => {
  const filteredData = !!filterData ? filterData.filter(data => selectedItems?.includes(data.key)) : []
  const result = filteredData.map(data => {
    return !!useKeyProperty ? data.key : data.display
  })

  return result || []
}

export const trackingHelper = {
  addTrackingEvent,
  buildFilterSelectTrackingEvent,
}

// ---- for testing purposes only -----------------

export const trackingHelperForTest = {
  convertCabinPosition,
  getCruise,
  getCabin,
  getPromos,
  getTransportationIndividual,
  getTransportationFlight,
  getTransportationFlexFlight,
  getTransportationRail,
  mapTrackingItem,
}
